// SPDX-License-Identifier: LicenseRef-Proprietary

import React, { useMemo } from 'react';
import type { 
  CategorizedResults as CategorizedResultsType, 
  PaperWithStatus
} from '../types/resultCategorization';
import { PaperListItem } from './PaperListItem';
import { SectionHeader } from './SectionHeader';
import './BinarySectionResults.css';

export interface BinarySectionResultsProps {
  categorizedResults: CategorizedResultsType;
  onPaperClick: (pmid: string) => void;
  selectedInputSource?: string | null;
  // Selection props
  selectedPmids?: string[];
  onSelectionChange?: (pmid: string, selected: boolean) => void;
  // State persistence
  expandedSections?: Set<string>;
  onSectionToggle?: (section: string) => void;
  // Expansion props
  expandedPapers?: Set<string>;
  onPaperExpansionChange?: (pmid: string, expanded: boolean) => void;
}

/**
 * Binary section results component that organizes papers into two sections:
 * 1. "AI suggests Relevant" - papers with any relevant parameters
 * 2. "AI suggests Not relevant" - papers with no relevant parameters
 * 
 * Relevant section is expanded by default, Not relevant is collapsed by default.
 */
export const BinarySectionResults: React.FC<BinarySectionResultsProps> = ({
  categorizedResults,
  onPaperClick,
  selectedInputSource,
  selectedPmids = [],
  onSelectionChange,
  expandedSections = new Set(['relevant']),
  onSectionToggle,
  expandedPapers = new Set(),
  onPaperExpansionChange
}) => {
  // Combine relevant papers (highly + somewhat relevant)
  const relevantPapers = useMemo(() => {
    return [
      ...categorizedResults.highlyRelevant,
      ...categorizedResults.somewhatRelevant
    ];
  }, [categorizedResults.highlyRelevant, categorizedResults.somewhatRelevant]);

  // Not relevant papers
  const notRelevantPapers = useMemo(() => {
    return categorizedResults.notRelevant;
  }, [categorizedResults.notRelevant]);

  // Failed papers go into the not relevant section (but not notAnalyzed)
  const allNotRelevantPapers = useMemo(() => {
    return [
      ...notRelevantPapers,
      ...categorizedResults.analysisFailed
    ];
  }, [notRelevantPapers, categorizedResults.analysisFailed]);

  // Not analyzed papers get their own section
  const notAnalyzedPapers = useMemo(() => {
    return categorizedResults.notAnalyzed;
  }, [categorizedResults.notAnalyzed]);

  const isRelevantExpanded = expandedSections.has('relevant');
  const isNotRelevantExpanded = expandedSections.has('not_relevant');
  const isNotAnalyzedExpanded = expandedSections.has('not_analyzed');

  const handleRelevantToggle = () => {
    if (onSectionToggle) {
      onSectionToggle('relevant');
    }
  };

  const handleNotRelevantToggle = () => {
    if (onSectionToggle) {
      onSectionToggle('not_relevant');
    }
  };

  const handleNotAnalyzedToggle = () => {
    if (onSectionToggle) {
      onSectionToggle('not_analyzed');
    }
  };

  const renderPaperList = (papers: PaperWithStatus[]) => {
    return papers.map(paper => {
      // Apply input source filter if active
      const isHiddenByFilter = selectedInputSource && 
        (!paper.aiBrief || !paper.aiBrief.potentialInputSources?.includes(selectedInputSource as any));

      if (isHiddenByFilter) return null;

      return (
        <PaperListItem
          key={paper.pmid}
          paper={paper}
          onClick={() => onPaperClick(paper.pmid)}
          triageBrief={paper.aiBrief}
          triageError={paper.triageError}
          isLoading={false}
          highlightInputSource={selectedInputSource}
          selectionMode={true}
          isSelected={selectedPmids.includes(paper.pmid)}
          onSelectionChange={onSelectionChange}
          isExpanded={expandedPapers.has(paper.pmid)}
          onExpansionChange={onPaperExpansionChange}
        />
      );
    });
  };

  return (
    <div className="binary-section-results">
      {/* Relevant Section */}
      <div className="binary-section-results__section">
        <SectionHeader
          title="AI Suggests Relevant"
          count={relevantPapers.length}
          isExpanded={isRelevantExpanded}
          onToggle={handleRelevantToggle}
          type="relevant"
          ariaControls="section-relevant-content"
        />
        {isRelevantExpanded && (
          <div 
            className="binary-section-results__section-content"
            id="section-relevant-content"
          >
            {relevantPapers.length > 0 ? (
              renderPaperList(relevantPapers)
            ) : (
              <div className="binary-section-results__empty-message">
                No relevant papers found
              </div>
            )}
          </div>
        )}
      </div>

      {/* Not Relevant Section */}
      <div className="binary-section-results__section">
        <SectionHeader
          title="AI Suggests Not Relevant"
          count={allNotRelevantPapers.length}
          isExpanded={isNotRelevantExpanded}
          onToggle={handleNotRelevantToggle}
          type="not_relevant"
          ariaControls="section-not-relevant-content"
        />
        {isNotRelevantExpanded && (
          <div
            className="binary-section-results__section-content"
            id="section-not-relevant-content"
          >
            {allNotRelevantPapers.length > 0 ? (
              renderPaperList(allNotRelevantPapers)
            ) : (
              <div className="binary-section-results__empty-message">
                No papers in this section
              </div>
            )}
          </div>
        )}
      </div>

      {/* Not Analyzed Section */}
      <div className="binary-section-results__section">
        <SectionHeader
          title="Not Analyzed by AI"
          count={notAnalyzedPapers.length}
          isExpanded={isNotAnalyzedExpanded}
          onToggle={handleNotAnalyzedToggle}
          type="not_analyzed"
          ariaControls="section-not-analyzed-content"
        />
        {isNotAnalyzedExpanded && (
          <div
            className="binary-section-results__section-content"
            id="section-not-analyzed-content"
          >
            {notAnalyzedPapers.length > 0 ? (
              renderPaperList(notAnalyzedPapers)
            ) : (
              <div className="binary-section-results__empty-message">
                No papers in this section
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
